import { useState } from 'react'
import { useStore } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedSummaryEvent } from '@/analytics/utils/medsummary-analytics'
import { useCreateNewAgentV2Chat } from '@/agent-v2/conversations/hooks/useNewAgentV2Chat'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { openConversationTab } from '@/chat-common/conversation-navigation/util/open-conversation-tab'
import { kRouteSegments } from '@/routing/paths'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { useAppDispatch } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { postInitAgentV2ConversationWithMedSummaryAsset } from '../fetch/post-init-agent-v2-conversation-with-med-summary-asset'

interface UseStartMedSummaryAssetChatReturn {
  isLoading: boolean
  startChat: () => Promise<void>
}

type UseStartMedSummaryAssetChatProps = {
  assetId: string
  matterId: string
}

/**
 * Starts a conversation grounded in the matter's living Medical Billing Summary,
 * seeded with a canned starter question, matching the chronology and timeline buttons.
 *
 * Opens in a new tab so the summary the user is reading stays put, matching
 * `useStartMedChronAssetChat`. That is why this creates the conversation without navigating and
 * builds the path itself rather than letting the hook navigate. When the popup is blocked the
 * conversation already exists, so it falls back to same-tab navigation instead of stranding it.
 */
export function useStartMedSummaryAssetChat(props: UseStartMedSummaryAssetChatProps): UseStartMedSummaryAssetChatReturn {
  const { assetId, matterId } = props
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const { createNewAgentV2Chat } = useCreateNewAgentV2Chat()
  const [isLoading, setIsLoading] = useState(false)

  const showFailureToast = () => {
    dispatch(
      openGlobalToast({
        type: GlobalToastType.ERROR,
        message: 'Failed to start chat. Please try again.',
        durationMs: kToastDurationMediumMs,
      })
    )
  }

  const startChat = async () => {
    if (isLoading) return
    setIsLoading(true)
    trackMedSummaryEvent(AnalyticsEvent.MedSummaryAssetChatClicked, { medSummaryAssetId: assetId, matterId }, trackEvent)

    try {
      const lifecycleGeneration = store.getState().agentV2ConversationsState.lifecycleGeneration
      const conversation = await createNewAgentV2Chat(false, matterId)
      // The hook swallows its own errors and returns undefined, including when a conversation
      // lifecycle reset raced this call. Either way there is nothing to open.
      if (!conversation) {
        showFailureToast()
        return
      }

      await postInitAgentV2ConversationWithMedSummaryAsset(conversation.id, assetId)
      if (store.getState().agentV2ConversationsState.lifecycleGeneration !== lifecycleGeneration) return

      const conversationPath = buildConversationPath(matterId, conversation.id, kRouteSegments.agentV2)
      openConversationTab(conversationPath, navigate)
    } catch (error) {
      console.error('Error creating medical billing summary chat:', error)
      showFailureToast()
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, startChat }
}
